import {
  type LeadDetailTabId,
  parseLeadDetailTab,
} from "@/components/leads/leadDetailTabs";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import type { AppLocationCodec } from "@/navigation/appLocation";
import { NAV_KEYS } from "@/navigation/keys";

export type LeadDetailLocation = {
  faClientId: string;
  tab: LeadDetailTabId;
  taskId?: string;
};

export function leadDetailDefaults(faClientId = ""): LeadDetailLocation {
  return { faClientId, tab: "overview" };
}

function readTaskId(params: Record<string, string | undefined>): string | undefined {
  const canonical = params[NAV_KEYS.taskId]?.trim();
  if (canonical) return canonical;
  const legacy = params[NAV_KEYS.taskLegacy]?.trim();
  return legacy || undefined;
}

function parseFaClientIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "leads" || !segments[1]) return undefined;
  return decodeURIComponent(segments[1]);
}

export function normalizeLeadDetail(loc: LeadDetailLocation): LeadDetailLocation {
  return {
    faClientId: loc.faClientId,
    tab: loc.tab,
    ...(loc.taskId ? { taskId: loc.taskId } : {}),
  };
}

function fromParams(params: Record<string, string | undefined>): LeadDetailLocation {
  const faClientId = params.faClientId?.trim() ?? "";
  const base = leadDetailDefaults(faClientId);
  const tab = parseLeadDetailTab(params[NAV_KEYS.tab]);
  if (tab) base.tab = tab;
  const taskId = readTaskId(params);
  if (taskId) base.taskId = taskId;
  return normalizeLeadDetail(base);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): LeadDetailLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  const pathId = parseFaClientIdFromPath(pathname);
  if (pathId) params.faClientId = pathId;
  return fromParams(params);
}

function toParams(loc: LeadDetailLocation): Record<string, string> {
  const n = normalizeLeadDetail(loc);
  const d = leadDetailDefaults(n.faClientId);
  const out: Record<string, string> = {};
  if (n.faClientId) out.faClientId = n.faClientId;
  if (n.tab !== d.tab) out[NAV_KEYS.tab] = n.tab;
  if (n.taskId) out[NAV_KEYS.taskId] = n.taskId;
  return out;
}

function toUrl(loc: LeadDetailLocation): string {
  const n = normalizeLeadDetail(loc);
  const path = `/leads/${encodeURIComponent(n.faClientId)}`;
  const d = leadDetailDefaults(n.faClientId);
  const query: Record<string, string> = {};
  if (n.tab !== d.tab) query[NAV_KEYS.tab] = n.tab;
  if (n.taskId) query[NAV_KEYS.taskId] = n.taskId;
  const qs = new URLSearchParams(query).toString();
  return qs ? `${path}?${qs}` : path;
}

export const leadDetailCodec: AppLocationCodec<LeadDetailLocation> = {
  appId: "lead",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

export function buildLeadDetailWindowUrl(
  faClientId: string,
  loc: Partial<Omit<LeadDetailLocation, "faClientId">> = {},
): string {
  return leadDetailCodec.toUrl({ ...leadDetailDefaults(faClientId), ...loc });
}

export function leadDetailShareUrl(faClientId: string, loc: Partial<Omit<LeadDetailLocation, "faClientId">>): string {
  return toAbsoluteUrl(buildLeadDetailWindowUrl(faClientId, loc));
}
